import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const A = "https://customer-assets-7cd3h4nn.emergentagent.net/job_apex-pro-rebuild/artifacts";
const PAIRS = [
  {
    label: "Concrete Walkway · Lehigh Valley",
    before: `${A}/qyd4yffv_20161016_132228-1_Original.webp`,
    after: `${A}/xj1mdee1_20161020_173918_Original.webp`,
  },
  {
    label: "Backyard Patio · Lehigh Valley",
    before: `${A}/q3n40zi6_20161020_131124_Original.webp`,
    after: `${A}/b0dedtlv_20161020_173907_Original.webp`,
  },
];

const Slider = ({ before, after, label }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onDown = (e) => { dragging.current = true; move(e.clientX ?? e.touches?.[0]?.clientX); };
  const onMove = (e) => { if (dragging.current) move(e.clientX ?? e.touches?.[0]?.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-md ring-1 ring-slate-200 select-none cursor-ew-resize"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      <img src={after} alt={`${label} after`} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <span className="absolute top-4 right-4 z-10 rounded-sm bg-white/90 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--apex-primary)]">After</span>

      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={`${label} before`}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: ref.current ? `${ref.current.getBoundingClientRect().width}px` : "100%" }}
          draggable="false"
        />
        <span className="absolute top-4 left-4 rounded-sm bg-[var(--apex-primary)]/90 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Before</span>
      </div>

      <div className="absolute inset-y-0 z-20 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full bg-white shadow-lg text-[var(--apex-primary)]">
          <MoveHorizontal size={20} />
        </div>
      </div>

      <div className="absolute bottom-4 left-4 z-10 rounded-sm bg-black/50 backdrop-blur px-3 py-1.5 text-sm font-semibold text-white">
        {label}
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const [active, setActive] = useState(0);
  return (
    <section className="bg-[var(--apex-primary)] relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-[0.04]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--apex-accent)]">Transformations</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-white mt-4 leading-tight">
              Drag to see the difference
            </h2>
          </div>
          <div className="flex gap-2">
            {PAIRS.map((p, i) => (
              <button
                key={p.label}
                data-testid={`ba-tab-${i}`}
                onClick={() => setActive(i)}
                className={`rounded-sm px-4 py-2 text-sm font-medium border transition-colors ${
                  active === i ? "bg-[var(--apex-accent)] border-[var(--apex-accent)] text-white" : "bg-white/5 border-white/15 text-slate-300 hover:border-white/40"
                }`}
              >
                {p.label.split(" · ")[0]}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Slider {...PAIRS[active]} />
        </motion.div>
      </div>
    </section>
  );
};
